import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Divider, Grid, Icon, IconButton, Modal, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { addDays, format } from "date-fns";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { ProvisionService } from "../../../../shared/services/api/provisoes/ProvisionService";

interface Props {
	open: boolean;
	borderoId: string;
	onClose: () => void;
	onSuccess: () => void;
}

const MIN_DAYS_FROM_TODAY = 15;

// Retirar suspensão (item 6) — volta o borderô pra "aberto". Motivo obrigatório + nova data
// limite (mesma regra de mínimo 15 dias do item 2). Só habilitado no menu quando o próprio
// Sinistro suspendeu (ver BorderoRowMenu) — suspensão do Financeiro é travada pro Sinistro.
export const ModalLiftSuspensionBordero = ({ open, borderoId, onClose, onSuccess }: Props) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();
	const minDueDate = format(addDays(new Date(), MIN_DAYS_FROM_TODAY), "yyyy-MM-dd");

	const [reason, setReason] = useState("");
	const [dueDate, setDueDate] = useState(minDueDate);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setReason("");
			setDueDate(minDueDate);
			setError(null);
		}
	}, [open]);

	const handleDueDateChange = (value: string) => {
		if (value && value < minDueDate) {
			setDueDate(minDueDate);
			return;
		}
		setDueDate(value);
	};

	const handleConfirm = async () => {
		if (!reason.trim()) {
			setError("Informe o motivo da retirada da suspensão.");
			return;
		}
		setError(null);
		setIsLoading(true);
		const result = await ProvisionService.borderoLiftSuspension(borderoId, { reason, dueDate: `${dueDate} 03:00:00` });
		setIsLoading(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", position: "top-right", messageOrContent: <ToastContent title="Erro" content={result.message} /> });
			return;
		}

		toastHandler.present({
			type: "success",
			position: "top-right",
			messageOrContent: <ToastContent title="Sucesso" content="Suspensão retirada — borderô voltou a ficar aberto." />,
		});
		onSuccess();
		onClose();
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 560,
					borderRadius: 3,
				}}
			>
				<LoadingComp isopen={isLoading} />

				<Grid container justifyContent="space-between" alignItems="center">
					<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
						Retirar Suspensão
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Grid>

				<Divider sx={{ my: 2 }} />

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<TextField
					fullWidth
					required
					label="Motivo"
					multiline
					minRows={3}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					sx={{ mb: 2 }}
				/>

				<TextField
					fullWidth
					required
					size="small"
					type="date"
					label="Nova data limite"
					value={dueDate}
					onChange={(e) => handleDueDateChange(e.target.value)}
					onBlur={(e) => handleDueDateChange(e.target.value)}
					inputProps={{ min: minDueDate }}
					InputLabelProps={{ shrink: true }}
					helperText="Mínimo de 15 dias a partir de hoje."
				/>

				<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
					<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
						Voltar
					</Button>
					<Button color="primary" variant="contained" startIcon={<Icon>play_circle</Icon>} onClick={handleConfirm} sx={{ minWidth: 140 }}>
						Confirmar
					</Button>
				</Grid>
			</Box>
		</Modal>
	);
};
